/**
 * VHAKACHA LISTING ATTRIBUTES  (web app mirror)
 * ============================================================
 * Mirror of  provider app → src/lib/listing-attributes.ts
 * Keep both files in sync — same values, same order, same coercion.
 *
 * The values below are what is stored on a listing. The labels are display
 * text and may change freely; the values may not, because they are what the
 * provider app writes and what this app matches on.
 *
 * WHY THIS EXISTS
 * ---------------
 * This app's listing page rendered a HARDCODED list of eight amenities —
 * Pool, Lodge, Wi-Fi, Activities, Snacks, Restaurant, Bar, Nightlife — on
 * every listing, identical for a river cruise and a national park. Nothing a
 * provider entered had any effect on it, because there was no field to enter
 * it into. Same for facilities, policies and seasonal rates: the new listing
 * page has sections for all of them and the schema had none.
 *
 * ── ONE AMENITY LIST, NOT TWO ────────────────────────────────
 *
 * "What this place offers" on Overview and the Facilities tab are one list
 * rendered twice — the first handful, then all of them grouped. Two lists
 * would guarantee they disagree, with no way for a traveller to tell which is
 * current.
 * ============================================================
 */

/* ────────────────────────────────────────────────────────────
 * Amenities
 * ──────────────────────────────────────────────────────────── */

export const AMENITIES = [
  { value: 'restaurant', label: 'Restaurant', group: 'Dining' },
  { value: 'bar', label: 'Bar / lounge', group: 'Dining' },
  { value: 'room_service', label: 'Room service', group: 'Dining' },
  { value: 'breakfast_included', label: 'Breakfast included', group: 'Dining' },
  { value: 'all_meals', label: 'All meals included', group: 'Dining' },
  { value: 'self_catering', label: 'Self-catering kitchen', group: 'Dining' },

  { value: 'pool', label: 'Swimming pool', group: 'Wellness' },
  { value: 'spa', label: 'Spa', group: 'Wellness' },
  { value: 'gym', label: 'Gym', group: 'Wellness' },
  { value: 'sauna', label: 'Sauna', group: 'Wellness' },

  { value: 'parking', label: 'Free parking', group: 'Practical' },
  { value: 'airport_shuttle', label: 'Airport shuttle', group: 'Practical' },
  { value: 'laundry', label: 'Laundry', group: 'Practical' },
  { value: 'air_conditioning', label: 'Air conditioning', group: 'Practical' },
  { value: 'backup_power', label: 'Backup power', group: 'Practical' },
  { value: 'safe', label: 'In-room safe', group: 'Practical' },
  { value: 'family_rooms', label: 'Family rooms', group: 'Practical' },
  { value: 'pets_allowed', label: 'Pets allowed', group: 'Practical' },

  { value: 'wifi', label: 'Wi-Fi', group: 'Connectivity' },
  { value: 'wifi_free', label: 'Free Wi-Fi', group: 'Connectivity' },
  { value: 'workspace', label: 'Desk / workspace', group: 'Connectivity' },

  { value: 'game_drives', label: 'Game drives', group: 'Activities' },
  { value: 'walking_safaris', label: 'Walking safaris', group: 'Activities' },
  { value: 'boat_cruises', label: 'Boat cruises', group: 'Activities' },
  { value: 'fishing', label: 'Fishing', group: 'Activities' },
  { value: 'guided_tours', label: 'Guided tours', group: 'Activities' },
  { value: 'campfire', label: 'Campfire / boma', group: 'Activities' },

  { value: 'step_free', label: 'Step-free entrance', group: 'Accessibility' },
  { value: 'ground_floor', label: 'Ground-floor rooms', group: 'Accessibility' },
  { value: 'accessible_bathroom', label: 'Accessible bathroom', group: 'Accessibility' },
];

/** Group order on the Facilities tab. Not alphabetical — most-asked first. */
export const AMENITY_GROUPS = [
  'Dining',
  'Wellness',
  'Activities',
  'Practical',
  'Connectivity',
  'Accessibility',
];

const AMENITY_BY_VALUE = new Map(AMENITIES.map((a) => [a.value, a]));

export function amenityLabel(value) {
  return AMENITY_BY_VALUE.get(value)?.label ?? value;
}

/**
 * Keep only amenities this vocabulary knows, in the vocabulary's own order.
 *
 * Order comes from the list above rather than from the stored array, so the
 * same lodge does not present its amenities in a different order after every
 * edit. Unknown values are dropped — they would render as a raw slug.
 */
export function knownAmenities(stored) {
  if (!Array.isArray(stored)) return [];
  const wanted = new Set(stored.filter((v) => typeof v === 'string'));
  return AMENITIES.filter((a) => wanted.has(a.value)).map((a) => a.value);
}

/** The same amenities, bucketed for the Facilities tab. Empty groups omitted. */
export function groupedAmenities(stored) {
  const values = new Set(knownAmenities(stored));
  return AMENITY_GROUPS.map((group) => ({
    group,
    amenities: AMENITIES.filter((a) => a.group === group && values.has(a.value)),
  })).filter((entry) => entry.amenities.length > 0);
}

/* ────────────────────────────────────────────────────────────
 * Gallery
 * ──────────────────────────────────────────────────────────── */

export const MEDIA_CATEGORIES = [
  { value: 'exterior', label: 'Exterior & grounds' },
  { value: 'rooms', label: 'Rooms & suites' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'dining', label: 'Dining' },
  { value: 'amenities', label: 'Amenities' },
  { value: 'activities', label: 'Activities' },
];

const MEDIA_CATEGORY_VALUES = new Set(MEDIA_CATEGORIES.map((c) => c.value));

/**
 * The gallery, from `media` where it exists and from `images` where it does not.
 *
 * NOTHING NEEDS MIGRATING FOR THIS TO WORK. Every listing in production has
 * `images` and no `media`; those photos become uncategorised entries, which is
 * honest — nobody has told us what they are. Guessing a category from the
 * filename or the position would fill the albums with wrong answers, and an
 * album that lies is worse than one that is missing.
 */
export function listingMedia(listing) {
  const doc = listing ?? {};

  if (Array.isArray(doc.media) && doc.media.length) {
    return doc.media
      .map((entry) => {
        if (typeof entry === 'string') return entry ? { url: entry } : null;
        if (!entry || typeof entry !== 'object') return null;
        if (typeof entry.url !== 'string' || !entry.url) return null;
        return {
          url: entry.url,
          ...(entry.category && MEDIA_CATEGORY_VALUES.has(entry.category)
            ? { category: entry.category }
            : {}),
          ...(entry.caption ? { caption: entry.caption } : {}),
        };
      })
      .filter(Boolean);
  }

  if (Array.isArray(doc.images)) {
    return doc.images.filter((url) => typeof url === 'string' && url).map((url) => ({ url }));
  }

  return [];
}

/**
 * Albums for the gallery: "All photos" first, then one per category that has
 * anything in it.
 *
 * An uncategorised photo appears in "All photos" and nowhere else, which is
 * why the album counts do not sum to the total. The alternative is an "Other"
 * album, which no traveller has ever wanted to open.
 */
export function mediaAlbums(listing) {
  const media = listingMedia(listing);
  if (!media.length) return [];

  const albums = [{ value: 'all', label: 'All photos', count: media.length }];

  for (const { value, label } of MEDIA_CATEGORIES) {
    const count = media.filter((m) => m.category === value).length;
    if (count) albums.push({ value, label, count });
  }

  return albums;
}

/** The photos in one album. `all` is everything, including uncategorised. */
export function mediaInAlbum(listing, album) {
  const media = listingMedia(listing);
  return album === 'all' ? media : media.filter((m) => m.category === album);
}


/* ────────────────────────────────────────────────────────────
 * Opening hours
 *
 * ENTERED BY THE OPERATOR, NOT TAKEN FROM GOOGLE.
 *
 * The reference design showed a "Business Hours" card sourced from Google
 * Places. Google's terms permit storing the `place_id` indefinitely and
 * latitude/longitude for 30 days — and nothing else. Hours, like ratings and
 * review text, must be fetched live on every render and never warehoused,
 * which means a per-page-view API call for a fact that changes twice a year.
 *
 * Asking the operator is better on every axis: no licence, no bill, no
 * latency, it works for the operators who have no Google presence at all, and
 * it is the operator's own statement about their own property.
 * ──────────────────────────────────────────────────────────── */

/** Monday first, as the design has it — not the JS Sunday-first convention. */
export const DAYS = [
  { key: 'mon', label: 'Monday', short: 'Mon' },
  { key: 'tue', label: 'Tuesday', short: 'Tue' },
  { key: 'wed', label: 'Wednesday', short: 'Wed' },
  { key: 'thu', label: 'Thursday', short: 'Thu' },
  { key: 'fri', label: 'Friday', short: 'Fri' },
  { key: 'sat', label: 'Saturday', short: 'Sat' },
  { key: 'sun', label: 'Sunday', short: 'Sun' },
];

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isValidTime(value) {
  return typeof value === 'string' && HH_MM.test(value);
}

/**
 * Keep only days that say something usable.
 *
 * A day with an opening time and no closing time is dropped rather than shown
 * as "From 08:00" — a traveller reading that has to guess when to arrive by,
 * and guessing wrong means a closed gate.
 */
export function normalizeOpeningHours(stored) {
  if (!stored || typeof stored !== 'object') return {};
  const result = {};

  for (const { key } of DAYS) {
    const day = stored[key];
    if (!day || typeof day !== 'object') continue;

    if (day.closed) { result[key] = { closed: true }; continue; }
    if (day.open24) { result[key] = { open24: true }; continue; }
    if (isValidTime(day.open) && isValidTime(day.close)) {
      result[key] = { open: day.open, close: day.close };
    }
  }

  return result;
}

export function formatDayHours(day) {
  if (!day) return '';
  if (day.closed) return 'Closed';
  if (day.open24) return 'Open 24 hours';
  // A range that ends before it starts runs past midnight — a bar closing at
  // 02:00 is normal, and rendering it that way is what a reader expects.
  return day.open && day.close ? `${day.open} – ${day.close}` : '';
}

/** One line per stated day, in week order. Empty when nothing is stated. */
export function statedOpeningHours(stored) {
  const hours = normalizeOpeningHours(stored);
  return DAYS.filter(({ key }) => hours[key]).map(({ key, label }) => ({
    key,
    label,
    hours: formatDayHours(hours[key]),
  }));
}

/* ────────────────────────────────────────────────────────────
 * Google Place ID
 * ──────────────────────────────────────────────────────────── */

/**
 * The ONE piece of Google Places data that may be stored indefinitely.
 *
 * Used to link a traveller to the place on Google Maps — a plain URL, not an
 * API call, so there is no licence obligation and no per-view cost. Ratings,
 * review text and hours are deliberately NOT stored; see the note above.
 */
export function googleMapsUrlFor(placeId) {
  if (typeof placeId !== 'string') return null;
  const id = placeId.trim();
  // Deliberately loose: Google documents place ids as opaque strings, so
  // rejecting one for its shape would refuse valid input.
  if (!id || /\s/.test(id) || id.length < 8) return null;

  return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(id)}`;
}

/* ────────────────────────────────────────────────────────────
 * Policies
 * ──────────────────────────────────────────────────────────── */

/**
 * Structured keys, free-text answers.
 *
 * Fixed keys so the same questions are answered in the same order on every
 * listing, and so `cancellation` can be shown again at checkout — which a
 * single free-text blob could not be.
 */
export const POLICY_FIELDS = [
  { key: 'checkIn', label: 'Check in', placeholder: 'From 14:00' },
  { key: 'checkOut', label: 'Check out', placeholder: 'By 10:00' },
  { key: 'cancellation', label: 'Cancellation', placeholder: 'Free cancellation up to 14 days before arrival…' },
  { key: 'children', label: 'Children', placeholder: 'Children of all ages welcome…' },
  { key: 'payment', label: 'Payment', placeholder: '30% deposit confirms the booking…' },
];

/** Only the policies actually answered, in the order above. */
export function statedPolicies(stored) {
  if (!stored || typeof stored !== 'object') return [];

  return POLICY_FIELDS.map(({ key, label }) => {
    const value = stored[key];
    return typeof value === 'string' && value.trim() ? { key, label, value: value.trim() } : null;
  }).filter(Boolean);
}

/* ────────────────────────────────────────────────────────────
 * Seasonal rates
 *
 * ⚠ DISPLAY ONLY. READ THIS BEFORE WIRING THEM TO ANYTHING.
 *
 * A booking is priced from `listing.cost` + `listing.priceUnit` and nothing
 * else — see utils/pricing.js, mirrored with the provider app and the
 * functions repo, and the only thing that decides what a traveller is charged
 * and what a provider is paid.
 *
 * These rates are what the operator publishes on the Pricing tab: indicative,
 * per season, and NOT what the platform charges. Wiring them into the booking
 * total is a deliberate, separate change that touches money in three repos.
 * Until then the UI must never present a season's price as the price of a
 * booking — quoting $580 and charging $450 is worse than showing no seasons.
 * ──────────────────────────────────────────────────────────── */

const MM_DD = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function isValidSeasonDate(value) {
  return typeof value === 'string' && MM_DD.test(value);
}

/** `MM-DD` → a sortable integer. Month-major, so 03-05 sorts below 03-12. */
function dayKey(mmdd) {
  const [month, day] = mmdd.split('-').map(Number);
  return month * 100 + day;
}

/**
 * Does this season cover the given date?
 *
 * HANDLES THE WRAP. "21 Dec – 05 Jan" is the festive season every lodge in
 * the country runs, and it ends in a lower `MM-DD` than it starts. A naive
 * `start <= d && d <= end` returns false for all twelve days of it — and the
 * one season most likely to be misquoted is the most expensive one.
 */
export function seasonCovers(rate, date) {
  if (!rate || !isValidSeasonDate(rate.startDate) || !isValidSeasonDate(rate.endDate)) return false;

  const start = dayKey(rate.startDate);
  const end = dayKey(rate.endDate);
  const target = (date.getMonth() + 1) * 100 + date.getDate();

  return start <= end
    ? target >= start && target <= end
    : target >= start || target <= end; // wraps through New Year
}

/**
 * The season covering `date`, or null.
 *
 * First match wins when ranges overlap, rather than cheapest or dearest — an
 * overlap is a mistake in the operator's own data, and silently picking a side
 * hides it. The provider form is where it gets surfaced.
 */
export function seasonCovering(rates, date) {
  return normalizeSeasonalRates(rates).find((rate) => seasonCovers(rate, date)) ?? null;
}

/**
 * Coerce whatever is stored into usable rates, dropping anything unusable.
 *
 * A rate with no price or a broken date range cannot be rendered and must not
 * be half-rendered: a season card reading "$NaN" or "undefined – 31 Oct" is
 * how a listing stops being trusted.
 */
export function normalizeSeasonalRates(stored) {
  if (!Array.isArray(stored)) return [];

  return stored
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null;

      if (!isValidSeasonDate(entry.startDate) || !isValidSeasonDate(entry.endDate)) return null;
      const price = typeof entry.price === 'number' ? entry.price : Number(entry.price);
      if (!Number.isFinite(price) || price <= 0) return null;

      return {
        id: entry.id || `season-${index}`,
        label: (entry.label || '').trim() || 'Season',
        startDate: entry.startDate,
        endDate: entry.endDate,
        ...(Number.isInteger(entry.minNights) && entry.minNights > 0
          ? { minNights: entry.minNights }
          : {}),
        price,
        currency: entry.currency || 'USD',
        priceUnit: entry.priceUnit || 'PER_NIGHT',
        inclusions: Array.isArray(entry.inclusions)
          ? entry.inclusions.filter((i) => typeof i === 'string' && i.trim()).map((i) => i.trim())
          : [],
      };
    })
    .filter(Boolean);
}
